import React from 'react';
import { Link } from 'react-router-dom';
import { Waves, ArrowRight, Activity } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PortfolioSwitcher from '../PortfolioSwitcher';
import { formatMoney } from '../../lib/format';

// iter 54: scale-aware hero copy. A single-location med-spa should not hear
// "you're moving markets" — they care about filling the waiting room.
//
// Money formatting goes through formatMoney() (iter-70 canonical module) so a
// brand whose projected number exceeds $1M never sees "$1256K" again. The
// iter-69 sweep missed this surface; iter-71 closes the gap and ESLint now
// guards against re-introducing hand-rolled "$XXXK" patterns.
const HERO_BY_SCALE = {
  single_location: {
    kicker: 'Your Waves Are',
    headline: 'Filling Your Room.',
    subTemplate: (proj, velocity, active) => (
      <>
        WAVE just drove{' '}
        <span data-highlight="roi">~{estimateWalkIns(proj)} projected walk-ins</span>{' '}
        with <span data-highlight="velocity">{velocity} pulse growth</span> across{' '}
        <span data-highlight="active">{active} active Wave{active !== 1 ? 's' : ''}</span>.
      </>
    ),
  },
  multi_location: {
    kicker: 'Your Waves Are',
    headline: 'Owning Your Regions.',
    subTemplate: (proj, velocity, active) => (
      <>
        WAVE is compounding{' '}
        <span data-highlight="roi">{formatMoney(proj)} projected regional revenue</span>{' '}
        with <span data-highlight="velocity">{velocity} pulse growth</span> across{' '}
        <span data-highlight="active">{active} active Wave{active !== 1 ? 's' : ''}</span>.
      </>
    ),
  },
  dtc_national: {
    kicker: 'Your Waves Are',
    headline: 'Moving Markets.',
    subTemplate: (proj, velocity, active) => (
      <>
        WAVE just generated{' '}
        <span data-highlight="roi">{formatMoney(proj)}</span> projected ShopMy revenue with{' '}
        <span data-highlight="velocity">{velocity} pulse growth</span> across{' '}
        <span data-highlight="active">{active} active Wave{active !== 1 ? 's' : ''}</span>.
      </>
    ),
  },
  enterprise_national: {
    kicker: 'Your Portfolio Is',
    headline: 'Synchronizing The Shelf.',
    subTemplate: (proj, velocity, active) => (
      <>
        WAVE is compounding{' '}
        <span data-highlight="roi">{formatMoney(proj)} projected retail-lift</span>{' '}
        with <span data-highlight="velocity">{velocity} co-op pulse growth</span> across{' '}
        <span data-highlight="active">{active} active Wave{active !== 1 ? 's' : ''}</span>.
      </>
    ),
  },
};

// Crude local-walk-in conversion: $60 AOV, 65% close → visit/visitor split.
function estimateWalkIns(projectedRevenue) {
  const visits = Math.max(0, Math.round((projectedRevenue || 0) / 60 / 0.65));
  return visits.toLocaleString();
}

export default function HeroCard({ brandLabel, hero, heroGlow }) {
  const { user } = useAuth();
  const copy = HERO_BY_SCALE[user?.brand_scale || 'dtc_national'] || HERO_BY_SCALE.dtc_national;

  return (
    <div className="relative overflow-hidden rounded-2xl px-7 py-8 lg:px-10 lg:py-10"
         style={{
           background: 'linear-gradient(135deg, #242730 0%, #1a1d26 45%, #242730 100%)',
           border: '1px solid #2E3240',
           boxShadow: heroGlow ? '0 0 60px rgba(0,212,255,0.18), inset 0 0 40px rgba(0,212,255,0.04)' : 'none',
           transition: 'box-shadow 2s ease',
         }}
         data-testid="home-hero"
         data-brand-scale={user?.brand_scale || 'dtc_national'}>
      {/* Ambient ripple */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[0, 1, 2].map(i => (
          <div key={`w-${i}`} className="absolute rounded-full border border-[#00D4FF]/10 animate-wave-ripple"
               style={{ width: `${140 + i * 50}px`, height: `${140 + i * 50}px`, top: '10%', right: `${5 + i * 4}%`, animationDelay: `${i * 0.6}s` }} />
        ))}
        <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full opacity-20 blur-3xl"
             style={{ background: 'radial-gradient(circle, #FF4D94, transparent 70%)' }} />
      </div>

      <div className="relative z-10">
        <div className="flex items-start justify-between gap-3 mb-1">
          <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">
            Welcome back, {brandLabel}
          </p>
          <PortfolioSwitcher compact />
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] pb-2 mb-4 tracking-tight"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #C9CCD4 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: heroGlow ? 'drop-shadow(0 0 18px rgba(0,212,255,0.3))' : 'none',
              transition: 'filter 2s ease',
            }}
            data-testid="hero-headline">
          {copy.kicker}<br />{copy.headline}
        </h1>

        <p className="text-sm lg:text-base text-gray-300 leading-relaxed mb-5 max-w-2xl" data-testid="hero-subheadline">
          <HighlightedSub
            node={copy.subTemplate(hero.projected, hero.velocity, hero.active)}
            heroGlow={heroGlow}
          />
        </p>

        {/* Design Contract addendum: one filled-pink primary per page.
            The hero's "Plan a new wave" is the home page primary. Live pulse
            link is tertiary (text-only). */}
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/build-wave" data-testid="launch-new-wave-btn">
            <button
              className="btn-wave px-7 py-3.5 rounded-xl bg-[#FF4D94] text-white font-black text-sm flex items-center gap-2 hover:scale-[1.03] transition-transform"
              style={{ boxShadow: '0 0 24px rgba(255,77,148,0.35)' }}
            >
              <Waves className="h-4 w-4" /> Plan a new wave
              <ArrowRight className="h-4 w-4" />
            </button>
          </Link>
          <Link to="/campaigns?filter=live" className="text-[11px] font-bold text-[#00D4FF] hover:underline flex items-center gap-1" data-testid="see-live-pulse-btn">
            <Activity className="h-3 w-3" /> See live waves →
          </Link>
        </div>
      </div>
    </div>
  );
}

// Wraps plain text with colored + glow classes on the three key highlight slots
// so the subheadline stays premium across all 4 scale variants.
function HighlightedSub({ node, heroGlow }) {
  const style = {
    filter: heroGlow ? 'drop-shadow(0 0 8px rgba(0,212,255,0.5))' : 'none',
    transition: 'filter 2s',
  };
  // Walk the React tree and recolor the slots labelled via data-highlight.
  return React.Children.map(node?.props?.children || [], (child) => {
    if (!child || typeof child === 'string') return child;
    const h = child.props?.['data-highlight'];
    if (h === 'roi')      return React.cloneElement(child, { className: 'text-green-400 font-black tabular-nums', style });
    if (h === 'velocity') return React.cloneElement(child, { className: 'text-[#FF4D94] font-black', style });
    if (h === 'active')   return React.cloneElement(child, { className: 'text-[#00D4FF] font-black' });
    return child;
  });
}
